import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, DollarSign, Calendar, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ModalPortal from './ModalPortal';

/**
 * CustomRequestModal Component
 * Allows Shippers to request custom work from Giggers
 */
const CustomRequestModal = ({
    isOpen,
    onClose,
    recipientId,
    conversationId,
    onSuccess
}) => {
  const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        budget: '',
        desiredDeliveryDays: 7,
        requirements: ['']
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addRequirement = () => {
        setFormData(prev => ({
            ...prev,
            requirements: [...prev.requirements, '']
        }));
    };

    const removeRequirement = (index) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.filter((_, i) => i !== index)
        }));
    };

    const updateRequirement = (index, value) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map((req, i) => i === index ? value : req)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a description');
            return;
        }
        if (!formData.budget || formData.budget <= 0) {
            toast.error('Please enter a valid budget');
            return;
        }

        try {
            setLoading(true);

            const payload = {
                recipientId,
                conversationId,
                title: formData.title,
                description: formData.description,
                budget: {
                    amount: parseFloat(formData.budget)
                },
                desiredDeliveryDays: parseInt(formData.desiredDeliveryDays),
                requirements: formData.requirements.filter(r => r.trim() !== '')
            };

            const response = await api.post('/requests', payload);

            toast.success('Custom request sent successfully!');

            if (onSuccess) {
                onSuccess(response.data.data);
            }

            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create request');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">{t('modals.request_custom_work', 'Request Custom Work')}</h2>
                        <p className="text-sm text-gray-500">{t('modals.tell_gigger_what_you_need', 'Tell the gigger exactly what you need done.')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Request Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="e.g., Need a Modern Business Logo"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength={100}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.title.length}/100 characters
                        </p>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Detailed Description *
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={t('modals.describe_project', 'Describe your project in detail...')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[150px]"
                            maxLength={1000}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.description.length}/1000 characters
                        </p>
                    </div>

                    {/* Budget and Delivery Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Budget */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <DollarSign className="w-4 h-4 inline mr-1" />
                                Your Budget (USD) *
                            </label>
                            <input
                                type="number"
                                value={formData.budget}
                                onChange={(e) => handleChange('budget', e.target.value)}
                                placeholder="300"
                                min="1"
                                step="0.01"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Maximum you're willing to pay
                            </p>
                        </div>

                        {/* Desired Delivery */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="w-4 h-4 inline mr-1" />
                                Needed in (Days) *
                            </label>
                            <input
                                type="number"
                                value={formData.desiredDeliveryDays}
                                onChange={(e) => handleChange('desiredDeliveryDays', e.target.value)}
                                min="1"
                                max="90"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                When do you need this completed?
                            </p>
                        </div>
                    </div>

                    {/* Requirements */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Specific Requirements (Optional)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                            List any must-have features, file formats, or deliverables
                        </p>

                        <div className="space-y-2">
                            {formData.requirements.map((req, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={req}
                                        onChange={(e) => updateRequirement(index, e.target.value)}
                                        placeholder="e.g., Vector files (AI, EPS)"
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {formData.requirements.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRequirement(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {formData.requirements.length < 10 && (
                            <button
                                type="button"
                                onClick={addRequirement}
                                className="mt-2 flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t('modals.add_requirement', 'Add requirement')}</span>
                            </button>
                        )}
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                        <p className="text-sm text-blue-800">
                            <strong>{t('modals.what_happens_next', 'What happens next?')}</strong>
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                            <li>{t('modals.gigger_can_accept', 'The gigger can accept, reject, or negotiate your request.')}</li>
                            <li>{t('modals.counter_offer', 'If they negotiate, you will receive a counter-offer.')}</li>
                            <li>This request expires in 24 hours</li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Sending...' : 'Send Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
};

export default CustomRequestModal;
